import React, { useState, useMemo, useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/radix-tabs';
import { LazySection } from '@/components/ui/LazySection';
import { Pill } from '@/components/ui/Pill';
import Link from 'next/link';
import { Building2, ExternalLink } from 'lucide-react';
import ProductFilterBar from './ProductFilterBar';
import ProductActiveFilters from './ProductActiveFilters';

interface FilterOption {
  key: string;
  label: string;
  options: string[];
  selected: string[];
  setSelected: (v: string[]) => void;
  loading?: boolean;
}

interface ProductSectionProps {
  title: string;
  products: any[];
  viewMode: 'card' | 'list';
  setViewMode: (mode: 'card' | 'list') => void;
  extractFields: (product: any) => any;
  technologyLookup: Record<string, string>;
  platformLookup: Record<string, string>;
  providerLookup: Record<string, string>;
  filters: FilterOption[];
  getSubtypeOptions: (productType: string) => string[];
  selectedSubtype: string;
  setSelectedSubtype: (subtype: string) => void;
  productType: string;
  showTabs?: boolean;
  showFilters?: boolean;
}

export default function ProductSection({
  title,
  products,
  viewMode,
  setViewMode,
  extractFields,
  technologyLookup,
  platformLookup,
  providerLookup,
  filters,
  getSubtypeOptions,
  selectedSubtype,
  setSelectedSubtype,
  productType,
  showTabs = true,
  showFilters = true
}: ProductSectionProps) {
  // Local state for this section
  const [showAllProducts, setShowAllProducts] = useState<boolean>(false);
  // Get subtype options for this product type
  const subtypeOptions = useMemo(() => {
    const options = getSubtypeOptions(productType);
    return ['All', ...options];
  }, [getSubtypeOptions, productType]);

  // Filter products
  const filteredProducts = useMemo(() => {
    let filtered = products;
    
    // Filter by selected subtype
    if (selectedSubtype !== 'All') {
      filtered = filtered.filter((product: any) => {
        let subtypeField: any;
        
        switch (productType) {
          case 'Assay Kits':
          case 'Assay Panels':
            subtypeField = product['Assay Type'];
            break;
          case 'Instruments':
            subtypeField = product['Instrument Type'];
            break;
          case 'Software':
            subtypeField = product['Software Type'];
            break;
          default:
            return true;
        }
        
        if (Array.isArray(subtypeField)) {
          return subtypeField.some((subtype: string) => subtype === selectedSubtype);
        } else if (typeof subtypeField === 'string') {
          return subtypeField === selectedSubtype;
        }
        
        return false;
      });
    }
    
    // Apply custom filters
    filtered = filtered.filter((product: any) => {
      return filters.every((filter) => {
        if (filter.selected.length === 0) return true;
        
        const fieldName = filter.key;
        const fieldValue = product[fieldName];
        
        if (Array.isArray(fieldValue)) {
          return fieldValue.some((value: any) => {
            // Handle lookup fields (Technology, Platform, Provider)
            if (fieldName === 'Technology') {
              const techId = typeof value === 'string' ? value : (value.id || value);
              const techName = technologyLookup[techId] || techId;
              return filter.selected.includes(techName);
            } else if (fieldName === 'Platform') {
              const platformId = typeof value === 'string' ? value : (value.id || value);
              const platformName = platformLookup[platformId] || platformId;
              return filter.selected.includes(platformName);
            } else if (fieldName === 'Provider') {
              const providerName = providerLookup[value] || value;
              return filter.selected.includes(providerName);
            } else {
              return filter.selected.includes(value);
            }
          });
        } else if (typeof fieldValue === 'string') {
          // Handle lookup fields for single values
          if (fieldName === 'Technology') {
            const techId = fieldValue;
            const techName = technologyLookup[techId] || techId;
            return filter.selected.includes(techName);
          } else if (fieldName === 'Platform') {
            const platformId = fieldValue;
            const platformName = platformLookup[platformId] || platformId;
            return filter.selected.includes(platformName);
          } else if (fieldName === 'Provider') {
            const providerName = providerLookup[fieldValue] || fieldValue;
            return filter.selected.includes(providerName);
          } else {
            return filter.selected.includes(fieldValue);
          }
        }
        
        return false;
      });
    });
    
    return filtered;
  }, [products, selectedSubtype, productType, filters, technologyLookup, platformLookup, providerLookup, extractFields]);

  // Calculate grid columns based on view mode
  const getGridColumns = () => {
    if (viewMode === 'list') return 1;
    // For card view, use responsive grid
    return 3; // lg:grid-cols-3
  };

  // Calculate products to show (2 rows worth)
  const productsToShow = showAllProducts 
    ? filteredProducts 
    : filteredProducts.slice(0, getGridColumns() * 2);
  
  const hasMoreProducts = filteredProducts.length > getGridColumns() * 2;

  // Reset show all state when filters change
  useEffect(() => {
    setShowAllProducts(false);
  }, [filters, selectedSubtype]);

  if (products.length === 0) {
    return null;
  }

  const renderProductCard = (product: any, idx: number, isListView: boolean = false) => {
    const { logoSrc, providerLogoSrc, productImageSrc, name, description, website, websiteUrl, location, slug, category } = extractFields(product);
    
    if (isListView) {
      return (
        <div key={product.id || idx} className="bg-white rounded-lg shadow-lg flex flex-col hover:shadow-xl transition-shadow duration-200">
          <div className="flex items-start">
            {/* Product Image Only */}
            <div className="flex-shrink-0 w-88 h-32 bg-neutral-100 rounded-l-lg overflow-hidden shadow-md" style={{ minWidth: '352px' }}>
              {productImageSrc ? (
                <img src={productImageSrc} alt={`${name} product`} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#A3A3A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2 17L12 22L22 17" stroke="#A3A3A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2 12L12 17L22 12" stroke="#A3A3A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0 p-6">
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-display font-bold text-xl text-primary-700 truncate">
                  {slug ? (
                    <Link href={`/products/${category}/${slug}`} className="hover:text-primary-600 transition-colors">
                      {name}
                    </Link>
                  ) : (
                    name
                  )}
                </h3>
                {websiteUrl && (
                  <a
                    href={websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-shrink-0 ml-2 text-neutral-500 hover:text-neutral-700 transition-colors"
                    aria-label={`Visit ${name} website`}
                  >
                    <ExternalLink size={16} />
                  </a>
                )}
              </div>
              
              {location && (
                <p className="text-sm text-neutral-600 mb-2 italic">{location}</p>
              )}
              
              {description && (
                <p className="text-sm text-neutral-700 mb-3 line-clamp-2">{description}</p>
              )}
              
              {/* Pills for product-specific fields */}
              <div className="flex flex-wrap gap-2">
                {filters.map((filter) => {
                  if (filter.selected.length === 0) return null;
                  
                  const fieldValue = product[filter.key];
                  if (!fieldValue) return null;
                  
                  const matchingValues: string[] = [];
                  
                  if (Array.isArray(fieldValue)) {
                    fieldValue.forEach((value: any) => {
                      let displayValue: string;
                      if (filter.key === 'Technology') {
                        const techId = typeof value === 'string' ? value : (value.id || value);
                        displayValue = technologyLookup[techId] || techId;
                      } else if (filter.key === 'Platform') {
                        const platformId = typeof value === 'string' ? value : (value.id || value);
                        displayValue = platformLookup[platformId] || platformId;
                      } else if (filter.key === 'Provider') {
                        displayValue = platformLookup[value] || value;
                      } else {
                        displayValue = value;
                      }
                      
                      if (filter.selected.includes(displayValue)) {
                        matchingValues.push(displayValue);
                      }
                    });
                  } else if (typeof fieldValue === 'string') {
                    let displayValue: string;
                    if (filter.key === 'Technology') {
                      const techId = fieldValue;
                      displayValue = technologyLookup[techId] || techId;
                    } else if (filter.key === 'Platform') {
                      const platformId = fieldValue;
                      displayValue = platformLookup[platformId] || platformId;
                    } else if (filter.key === 'Provider') {
                      displayValue = platformLookup[fieldValue] || fieldValue;
                    } else {
                      displayValue = fieldValue;
                    }
                    
                    if (filter.selected.includes(displayValue)) {
                      matchingValues.push(displayValue);
                    }
                  }
                  
                  return [...new Set(matchingValues)].map((value: string, i: number) => (
                    <Pill key={`${filter.key}-${i}`} type="productCategory">{value}</Pill>
                  ));
                })}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Card view
    const cardContent = (
      <div className="bg-white rounded-lg shadow-lg h-full flex flex-col hover:shadow-xl transition-shadow duration-200 cursor-pointer">
        {/* Product Image Only */}
        <div className="w-full bg-neutral-100 rounded-t-lg overflow-hidden shadow-md" style={{ height: 200 }}>
          {productImageSrc ? (
            <img 
              src={productImageSrc} 
              alt={`${name} product`} 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#A3A3A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 17L12 22L22 17" stroke="#A3A3A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 12L12 17L22 12" stroke="#A3A3A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          )}
        </div>
        
        {/* Content Section */}
        <div className="flex-1 flex flex-col p-6">
          <h3 className="font-display font-bold text-xl mb-2 text-primary-700 line-clamp-2">
            {name}
          </h3>
          
          {location && (
            <p className="text-sm text-neutral-600 mb-2 italic">
              {location}
            </p>
          )}
          
          {description && (
            <p className="text-sm text-neutral-700 mb-4 line-clamp-3 flex-1">
              {description}
            </p>
          )}
          
          {/* Pills Section */}
          <div className="mt-auto">
            <div className="flex flex-wrap gap-2">
              {filters.map((filter) => {
                if (filter.selected.length === 0) return null;
                
                const fieldValue = product[filter.key];
                if (!fieldValue) return null;
                
                const matchingValues: string[] = [];
                
                if (Array.isArray(fieldValue)) {
                  fieldValue.forEach((value: any) => {
                    let displayValue: string;
                    if (filter.key === 'Technology') {
                      const techId = typeof value === 'string' ? value : (value.id || value);
                      displayValue = technologyLookup[techId] || techId;
                    } else if (filter.key === 'Platform') {
                      const platformId = typeof value === 'string' ? value : (value.id || value);
                      displayValue = platformLookup[platformId] || platformId;
                    } else if (filter.key === 'Provider') {
                      displayValue = platformLookup[value] || value;
                    } else {
                      displayValue = value;
                    }
                    
                    if (filter.selected.includes(displayValue)) {
                      matchingValues.push(displayValue);
                    }
                  });
                } else if (typeof fieldValue === 'string') {
                  let displayValue: string;
                  if (filter.key === 'Technology') {
                    const techId = fieldValue;
                    displayValue = technologyLookup[techId] || techId;
                  } else if (filter.key === 'Platform') {
                    const platformId = fieldValue;
                    displayValue = platformLookup[platformId] || platformId;
                  } else if (filter.key === 'Provider') {
                    displayValue = platformLookup[fieldValue] || fieldValue;
                  } else {
                    displayValue = fieldValue;
                  }
                  
                  if (filter.selected.includes(displayValue)) {
                    matchingValues.push(displayValue);
                  }
                }
                
                return [...new Set(matchingValues)].slice(0, 2).map((value: string, i: number) => (
                  <Pill key={`${filter.key}-${i}`} type="productCategory">{value}</Pill>
                ));
              })}
            </div>
          </div>
        </div>
      </div>
    );

    // If we have a slug, make the entire card clickable
    if (slug) {
      return (
        <Link
          key={product.id || idx}
          href={`/products/${category}/${slug}`}
          className="block focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-lg"
          aria-label={`View details for ${name}`}
        >
          {cardContent}
        </Link>
      );
    }

    return (
      <div key={product.id || idx}>
        {cardContent}
      </div>
    );
  };

  return (
    <LazySection className="mb-12">
      <div className="mb-6">
        <h2 className="font-display text-3xl font-bold text-primary-500 mb-4">{title}</h2>
      </div>
      
      {/* Filter Bar */}
      {showFilters && (
        <>
          <ProductFilterBar filters={filters} />
          
          {/* Active Filter Pills */}
          <ProductActiveFilters
            filters={filters}
            onClearFilters={() => {
              filters.forEach(filter => filter.setSelected([]));
              setShowAllProducts(false);
            }}
            viewMode={viewMode}
            setViewMode={setViewMode}
          />
        </>
      )}
      
      {/* Subtype Tabs */}
      {showTabs && subtypeOptions.length > 1 && (
        <div className="mb-6">
          <Tabs value={selectedSubtype} onValueChange={setSelectedSubtype} className="w-full">
            <TabsList className="mb-6 flex gap-6 p-2 bg-neutral-100 rounded-lg justify-start">
              {subtypeOptions.map((type) => (
                <TabsTrigger 
                  key={type} 
                  value={type} 
                  className="font-display flex items-center gap-2 px-4 py-3 min-w-[120px] justify-center data-[state=active]:bg-primary-600 data-[state=active]:text-white data-[state=active]:shadow-md"
                >
                  {type}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      )}
      
      <div className="mt-6">
        {filteredProducts.length > 0 ? (
          <>
            {viewMode === 'list' ? (
              // List View
              <div className="space-y-6">
                {productsToShow.map((product: any, idx: number) => 
                  renderProductCard(product, idx, true)
                )}
              </div>
            ) : (
              // Grid View
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {productsToShow.map((product: any, idx: number) => 
                  renderProductCard(product, idx, false)
                )}
              </div>
            )}
            
            {/* Show More / Show Less Button */}
            {hasMoreProducts && (
              <div className="mt-6">
                <button
                  onClick={() => setShowAllProducts(!showAllProducts)}
                  className="w-full px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 border border-primary-200 rounded-md transition-colors"
                >
                  {showAllProducts 
                    ? 'Show Less' 
                    : `Show ${filteredProducts.length - (getGridColumns() * 2)} More ${productType}`
                  }
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-muted-foreground">No {title.toLowerCase()} found for the selected filters.</div>
        )}
      </div>
    </LazySection>
  );
}
